import React, { useState, useEffect, useRef } from "react";
import {
  Dialog as MuiDialog,
  DialogTitle as MuiDialogTitle,
  DialogContent as MuiDialogContent,
  IconButton as MuiIconButton,
  Typography,
  Button,
  Chip,
  Divider,
  Grid,
} from "@mui/material";
import { Box, styled } from "@mui/system";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import FormikTextInput from "@/components/forms/FormikTextInput";
import FormikSelectInput from "@/components/forms/FormikSelectInput";
import FormikDatePicker from "@/components/forms/FormikDatePicker";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { DownloadIcon } from "@/icons";
import { useLazyDocumentDownloadQuery } from "@/api/services/profile/profile_/OtherDocumentsApi";
import { useHrFetchBasicDetailsQuery, useHrPutBasicDetailsMutation } from "@/api/services/profile/profile_/BasicDetailsApi";
import { useHrFetchAccountInfoQuery, useHrPutAccountInfoMutation } from "@/api/services/profile/profile_/AccountInforamtionApi";
import { useFetchSettingsRowsQuery } from "@/api/services/Settings/business/Business";
import Loader from "../loader";
import dayjs from "dayjs";
import { decryptData } from "@/utils/cryptoHelpers";
import PersonIcon from "@mui/icons-material/Person";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import WcIcon from "@mui/icons-material/Wc";
import BloodtypeIcon from "@mui/icons-material/Bloodtype";
import CakeIcon from "@mui/icons-material/Cake";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import TempleHinduIcon from "@mui/icons-material/TempleHindu";
import FavoriteIcon from "@mui/icons-material/Favorite";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import PinIcon from "@mui/icons-material/Pin";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import StoreIcon from "@mui/icons-material/Store";
import BadgeIcon from "@mui/icons-material/Badge";
import { toast } from "@/components/toastify";


// Move styled components outside the functional component
const Dialog = styled(MuiDialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    overflowX: "hidden",
    fontFamily: "Lato, sans-serif",
  },
}));

const DialogTitle = styled(MuiDialogTitle)(({ theme }) => ({
  position: "relative",
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: "18px !important",
  lineHeight: "21.6px",
}));

const IconButton = styled(MuiIconButton)(({ theme }) => ({
  position: "absolute",
  right: 10,
  top: 8,
  color: "white",
}));

const DialogContent = styled(MuiDialogContent)(({ theme }) => ({
  overflowX: "hidden",
  overflowY: "auto",
  padding: theme.spacing(2),
  fontFamily: "Lato, sans-serif",
}));

const BasicDetailsSidebar = ({ empId, folder, employeeRole }) => {
  const { data: basicInfo, isLoading: basicLoading } =
    useHrFetchBasicDetailsQuery(empId, { skip: !empId });
  const { data: accountInfo, isLoading: accountLoading } =
    useHrFetchAccountInfoQuery(empId, { skip: !empId });
  const [hrUpdateBasicDetails] = useHrPutBasicDetailsMutation();
  const [hrUpdateAccountInfo] = useHrPutAccountInfoMutation();
  const { data: bloodGroup = [] } = useFetchSettingsRowsQuery("Blood Group");

  const [editMode, setEditMode] = useState(false);
  const [editInitialValues, setEditInitialValues] = useState({});
  const [editAccountMode, setEditAccountMode] = useState(false);
  const [editAccountInitialValues, setEditAccountInitialValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [savingAccount, setSavingAccount] = useState(false);
  const d = basicInfo?.data || {};
  const a = accountInfo?.data?.bankDetails?.[0] || {};
  const acc = accountInfo?.data || {};

  const canEdit = employeeRole === "HRSS";

  const handleAccountEditOpen = () => {
    setEditAccountInitialValues({
      accountHolderName: a.accountHolderName || "",
      bankName: a.bankName || "",
      accountNumber: a.accountNumber || "",
      confirmAccountNumber: a.confirmAccountNumber || a.accountNumber || "",
      ifscCode: a.ifscCode || "",
      accountMobileNumber: a.accountMobileNumber || "",
      branchName: a.branchName || "",
      upiId: a.upiId || "",
      uanNo: acc.uanNo || "",
      pfNo: acc.pfNo || "",
      cancelledChequeLeaf: acc.cancelledChequeLeaf || "",
    });
    setEditAccountMode(true);
  };

  const accountValidationSchema = Yup.object({
    accountNumber: Yup.string().matches(/^\d{9,18}$/, "Account Number must be 9 to 18 digits"),
    confirmAccountNumber: Yup.string().test("match", "Account Numbers must match", function (val) {
      return !val || val === this.parent.accountNumber;
    }),
  });

  const handleAccountSave = async (values) => {
    setSavingAccount(true);
    try {
      const response = await hrUpdateAccountInfo({
        empId,
        formData: { formName: "Add Account Information", formFields: values },
      }).unwrap();
      toast("success", response?.data || "Account updated successfully");
      setEditAccountMode(false);
    } catch (err) {
      toast("error", err?.data?.message || "Update failed");
    } finally {
      setSavingAccount(false);
    }
  };

  const handleEditOpen = () => {
    setEditInitialValues({
      firstName: d.firstName || "",
      middleName: d.middleName || "",
      lastName: d.lastName || "",
      gender: d.gender || "",
      bloodGroup: d.bloodGroup || "",
      dob: d.dob ? new Date(d.dob) : null,
      panNo: d.panNo ? decryptData(d.panNo) : "",
      aadhaarCardNo: d.aadhaarCardNo ? decryptData(d.aadhaarCardNo) : "",
    });
    setEditMode(true);
  };

  const basicValidationSchema = Yup.object({
    firstName: Yup.string().required("Please enter the First Name"),
    lastName: Yup.string().required("Please enter the Last Name"),
    dob: Yup.date().nullable(),
    ...(folder === "PAN" ? {
      panNo: Yup.string().matches(/^[A-Z]{5}[0-9]{4}[A-Z]$/, "Invalid PAN format (e.g., ABCDE1234F)"),
    } : {}),
    ...(folder === "AADHAAR_CARD" || folder === "AADHAAR_CARD_BACK" ? {
      aadhaarCardNo: Yup.string().matches(/^\d{12}$/, "Enter a valid 12-digit Aadhaar number"),
    } : {}),
  });

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const formatDate = (val) => new Date(val).toLocaleDateString("en-GB");
      const formFields = {
        roleType: "others",
        prohireRole: "",
        firstName: values.firstName,
        middleName: values.middleName,
        lastName: values.lastName,
        bloodGroup: values.bloodGroup,
        gender: values.gender,
        maritalStatus: d.maritalStatus || "Single",
        age: d.age || "",
        placeOfBirth: d.placeOfBirth || "",
        religion: d.religion || "",
        identificationMarks: d.identificationMarks || "",
        aadhaarCardNo: values.aadhaarCardNo,
        panNo: values.panNo,
        primary: d.skills?.primary || [],
        secondary: d.skills?.secondary || [],
        languages: d.languages || [],
        dob: values.dob ? formatDate(values.dob) : "",
        preferredDob: d.preferredDob ? formatDate(d.preferredDob) : "",
        passportNumber: d.passportNumber || "",
      };
      const response = await hrUpdateBasicDetails({ formData: { formName: "Add Basic Details", formFields }, empId: String(empId) }).unwrap();
      toast("success", response?.data || "Updated successfully");
      setEditMode(false);
    } catch (err) {
      toast("error", err?.data?.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const basicFields = [
    {
      label: "Name",
      value: [d.firstName, d.middleName, d.lastName].filter(Boolean).join(" "),
      icon: <PersonIcon fontSize="small" />,
      span: 2,
    },
    {
      label: "Gender",
      value: d.gender,
      icon: <WcIcon fontSize="small" />,
      span: 1,
    },
    {
      label: "Blood Group",
      value: d.bloodGroup,
      icon: <BloodtypeIcon fontSize="small" />,
      span: 1,
    },
    {
      label: "Date of Birth",
      value: d.dob ? dayjs(d.dob).format("DD-MM-YYYY") : "",
      icon: <CakeIcon fontSize="small" />,
      span: 1,
    },
    // { label: "Place of Birth", value: d.placeOfBirth, icon: <LocationOnIcon fontSize="small" />, span: 1 },
    // { label: "Religion", value: d.religion, icon: <TempleHinduIcon fontSize="small" />, span: 1 },
    // { label: "Marital Status", value: d.maritalStatus, icon: <FavoriteIcon fontSize="small" />, span: 1 },
    {
      label: "Date of Joining",
      value: d.doj ? dayjs(d.doj).format("DD-MM-YYYY") : "",
      icon: <HowToRegIcon fontSize="small" />,
      span: 1,
    },
    ...(folder === "PAN"
      ? [
          {
            label: "PAN Number",
            value: d.panNo ? decryptData(d.panNo) : "",
            icon: <CreditCardIcon fontSize="small" />,
            span: 1,
          },
        ]
      : folder === "AADHAAR_CARD" || folder === "AADHAAR_CARD_BACK"
        ? [
            {
              label: "Aadhaar Number",
              value: d.aadhaarCardNo ? decryptData(d.aadhaarCardNo) : "",
              icon: <FingerprintIcon fontSize="small" />,
              span: 1,
            },
          ]
        : []),
  ];

  const accountFields = [
    {
      label: "Account Holder Name",
      value: a.accountHolderName,
      icon: <AccountCircleIcon fontSize="small" />,
      span: 2,
    },
    {
      label: "Account Number",
      value: a.accountNumber,
      icon: <PinIcon fontSize="small" />,
      span: 1,
    },
    {
      label: "Bank Name",
      value: a.bankName,
      icon: <AccountBalanceIcon fontSize="small" />,
      span: 1,
    },
    {
      label: "IFSC",
      value: a.ifscCode,
      icon: <BadgeIcon fontSize="small" />,
      span: 1,
    },
    {
      label: "Branch Name",
      value: a.branchName,
      icon: <StoreIcon fontSize="small" />,
      span: 1,
    },
    {
      label: "Mobile Number",
      value: a.accountMobileNumber,
      icon: <PhoneAndroidIcon fontSize="small" />,
      span: 1,
    },
    {
      label: "UAN Number",
      value: acc.uanNo,
      icon: <BadgeIcon fontSize="small" />,
      span: 1,
    },
    {
      label: "PF Number",
      value: acc.pfNo,
      icon: <BadgeIcon fontSize="small" />,
      span: 1,
    },
  ];

  if (basicLoading || accountLoading) return <Loader />;

  const FieldCard = ({ label, value, icon }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: 1.2,
        borderRadius: 2,
        backgroundColor: "#FFFFFF",
        border: "1px solid #E2E8F0",
        boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
        transition: "all 0.2s ease",
        "&:hover": { backgroundColor: "#F8FAFC", borderColor: "#CBD5E1" },
        height: "100%",
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 32,
          height: 32,
          borderRadius: "50%",
          backgroundColor: "#F1F5F9",
          color: "#64748B",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{ color: "#94A3B8", fontSize: "10px", fontWeight: 500 }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            color: "#334155",
            fontWeight: 600,
            fontSize: "12px",
            wordBreak: "break-word",
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );

  const renderFields = (fields) => (
    <Box
      sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1 }}
    >
      {fields
        .filter((f) => f.value)
        .map(({ label, value, icon, span }) => (
          <Box
            key={label}
            sx={{ gridColumn: span === 2 ? "span 2" : "span 1" }}
          >
            <FieldCard label={label} value={value} icon={icon} />
          </Box>
        ))}
    </Box>
  );

  return (
    <Box
      sx={{
        overflowY: "auto",
        height: "100%",
        borderRight: "1px solid #E2E8F0",
        backgroundColor: "#F8FAFC",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.3,
          backgroundColor: "#F1F5F9",
          borderBottom: "1px solid #E2E8F0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PersonIcon sx={{ color: "#64748B", fontSize: 18 }} />
          <Typography sx={{ fontWeight: 600, fontSize: "13px", color: "#334155" }}>
            Employee Details
          </Typography>
        </Box>
        {canEdit && !editMode && (
          <MuiIconButton size="small" onClick={handleEditOpen} sx={{ color: "#64748B" }}>
            <EditIcon fontSize="small" />
          </MuiIconButton>
        )}
      </Box>
      {editMode ? (
        <Formik
          initialValues={editInitialValues}
          validationSchema={basicValidationSchema}
          onSubmit={handleSave}
          enableReinitialize
        >
          {() => (
            <Form>
              <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                <FormikTextInput name="firstName" label="First Name" placeholder="First Name" inputRestriction="text" required />
                <FormikTextInput name="middleName" label="Middle Name" placeholder="Middle Name" inputRestriction="text" />
                <FormikTextInput name="lastName" label="Last Name" placeholder="Last Name" inputRestriction="text" required />
                <FormikSelectInput
                  name="gender" label="Gender" placeholder="Select Gender"
                  options={["Male", "Female", "Other"].map((g) => ({ value: g, label: g }))}
                />
                <FormikSelectInput
                  name="bloodGroup" label="Blood Group" placeholder="Select Blood Group"
 options={bloodGroup?.data?.map((c) => ({
                      value: c.name, // or c.name, whichever you want to store
                      label: c.name,
                    }))}                />
                <FormikDatePicker name="dob" label="Date of Birth" disableFuture />
                {folder === "PAN" && (
                  <FormikTextInput name="panNo" label="PAN Number" placeholder="ABCDE1234F" inputRestriction="alphanumeric" maxLength={10} />
                )}
                {(folder === "AADHAAR_CARD" || folder === "AADHAAR_CARD_BACK") && (
                  <FormikTextInput name="aadhaarCardNo" label="Aadhaar Number" placeholder="12-digit number" inputRestriction="numeric" maxLength={12} />
                )}
                <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                  <Button size="small" variant="outlined" onClick={() => setEditMode(false)} disabled={saving}>Cancel</Button>
                  <Button size="small" variant="contained" type="submit" disabled={saving}>{saving ? "Saving..." : "Save"}</Button>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      ) : <Box sx={{ p: 1.5 }}>{renderFields(basicFields)}</Box>}
      {folder === "BANK_PASSBOOK" && (
        <>
          <Box
            sx={{
              px: 2,
              py: 1.3,
              backgroundColor: "#F1F5F9",
              borderTop: "1px solid #E2E8F0",
              borderBottom: "1px solid #E2E8F0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AccountBalanceIcon sx={{ color: "#64748B", fontSize: 18 }} />
              <Typography sx={{ fontWeight: 600, fontSize: "13px", color: "#334155" }}>
                Account Information
              </Typography>
            </Box>
            {canEdit && !editAccountMode && (
              <MuiIconButton size="small" onClick={handleAccountEditOpen} sx={{ color: "#64748B" }}>
                <EditIcon fontSize="small" />
              </MuiIconButton>
            )}
          </Box>
          {editAccountMode ? (
            <Formik
              initialValues={editAccountInitialValues}
              validationSchema={accountValidationSchema}
              onSubmit={handleAccountSave}
              enableReinitialize
            >
              {() => (
                <Form>
                  <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <FormikTextInput name="accountHolderName" label="Account Holder Name" placeholder="Account Holder Name" inputRestriction="text" />
                    <FormikTextInput name="bankName" label="Bank Name" placeholder="Bank Name" inputRestriction="alphanumeric" />
                    <FormikTextInput name="accountNumber" label="Account Number" placeholder="9 to 18 digits" inputRestriction="numeric" maxLength={18} />
                    <FormikTextInput name="confirmAccountNumber" label="Confirm Account Number" placeholder="Confirm Account Number" inputRestriction="numeric" maxLength={18} />
                    <FormikTextInput name="ifscCode" label="IFSC Code" placeholder="HDFC0000123" inputRestriction="alphanumeric" maxLength={11} />
                    <FormikTextInput name="accountMobileNumber" label="Mobile Number" placeholder="Mobile Number" inputRestriction="numeric" maxLength={10} />
                    <FormikTextInput name="branchName" label="Branch Name" placeholder="Branch Name" inputRestriction="text" />
                    <FormikTextInput name="uanNo" label="UAN Number" placeholder="UAN Number" inputRestriction="numeric" maxLength={12} />
                    <FormikTextInput name="pfNo" label="PF Number" placeholder="PF Number" inputRestriction="alphanumeric" maxLength={22} />
                    <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                      <Button size="small" variant="outlined" onClick={() => setEditAccountMode(false)} disabled={savingAccount}>Cancel</Button>
                      <Button size="small" variant="contained" type="submit" disabled={savingAccount}>{savingAccount ? "Saving..." : "Save"}</Button>
                    </Box>
                  </Box>
                </Form>
              )}
            </Formik>
          ) : <Box sx={{ p: 1.5 }}>{renderFields(accountFields)}</Box>}
        </>
      )}
    </Box>
  );
};

const DocViewModal = ({
  folder = "",
  file = [],
  open = false,
  setOpen = () => {},
  width = "520px",
  moduleName = "",
  apiPath = "",
  qcStatus = null,
  onQcAction = () => {},
  empIdGenerateStatus = false,
  empId = null,
}) => {
  const [blobToPdf, setBlobToPdf] = useState(null);
  const [imageUrls, setImageUrls] = useState("");
  const [isLoaded, setIsLoaded] = useState(true);
  const [unsupportedFormat, setUnsupportedFormat] = useState(false);
  const loginDetails = JSON.parse(localStorage.getItem("loginDetails"));
  const employeeRole = loginDetails?.userDetails?.roleType;
  const [documentDownload] = useLazyDocumentDownloadQuery();
  const downloadLinkRef = useRef(null);

  const handleClose = () => {
    setOpen(false);
    setIsLoaded(false);
    setUnsupportedFormat(false);
  };

  const token = JSON.parse(localStorage.getItem("loginDetails")).token;

  const handleFileApi = async (folder, fileName, fileType) => {
    console.log(folder, fileName, fileType, "api call");
    try {
      setIsLoaded(true);

      const response = await fetch(
        `${process.env.REACT_APP_DEV_IMG_URL}${apiPath}${folder}/${fileName}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) throw new Error(`Failed to fetch ${fileType}`);

      const blob = await response.blob();

      if (fileType === "pdf") {
        const pdfBlob = new Blob([blob], { type: "application/pdf" });
        const blobUrl = URL.createObjectURL(pdfBlob);
        setBlobToPdf(`${blobUrl}#toolbar-0`);
      } else if (fileType === "image") {
        const imageUrl = URL.createObjectURL(blob);
        setImageUrls(imageUrl);
      }
    } catch (error) {
      console.error(`Error fetching ${fileType}:`, error);
      setIsLoaded(false);
    } finally {
      setIsLoaded(false);
    }
  };

  const handleDownload = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    console.log(file, "file");
    let fileNameValue = "";
    if (Array.isArray(file)) {
      fileNameValue = file[0]; // Take first element from array
    } else if (typeof file === "string") {
      fileNameValue = file; // Directly the filename
    } else {
      // console.error("Invalid file format:", file);
      // return;
    }
    console.log("fileNameValue", fileNameValue);

    try {
      const response = await documentDownload({
        folder,
        file: fileNameValue,
      }).unwrap();

      const url = window.URL.createObjectURL(response); // response is already a Blob
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileNameValue);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  useEffect(() => {
    if (!open) return;
    const supportedImageExts = [
      "jpg",
      "jpeg",
      "png",
      "gif",
      "bmp",
      "webp",
      "svg",
    ];

    const processFile = (fileName) => {
      const fileExtension = fileName.split(".").pop().toLowerCase();
      if (fileExtension === "pdf") {
        handleFileApi(folder, fileName, "pdf");
      } else if (supportedImageExts.includes(fileExtension)) {
        handleFileApi(folder, fileName, "image");
      } else {
        setIsLoaded(false);
        setUnsupportedFormat(true);
      }
    };

    const files = Array.isArray(file) ? file : [file];
    if (files.length > 0 && files[0]) {
      files.forEach((item) => processFile(item));
    } else {
      setIsLoaded(false);
    }
  }, [file, folder, open]);

  return (
    <>
      <a ref={downloadLinkRef} style={{ display: "none" }} />
      {/* {isLoaded && ( */}
      <Dialog
        fullWidth
        scroll="body"
        open={open}
        onClose={handleClose}
        maxWidth={width || "lg"}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>{folder || "File Preview"}</span>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {file &&
                String(file).toLowerCase().endsWith(".pdf") === false &&
                employeeRole === "HRSS" &&
                moduleName === "Other Documents" && (
                  <IconButton
                    edge="start"
                    color="primary"
                    onClick={handleDownload}
                    aria-label="download"
                    sx={{ mr: 2 }}
                  >
                    <DownloadIcon />
                  </IconButton>
                )}

              <IconButton
                edge="end"
                color="primary"
                onClick={handleClose}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ display: "flex", height: "600px" }}>
            {(employeeRole === "Quality Check" || employeeRole === "HRSS") &&
              empId && (
                <Box sx={{ width: "35%", minWidth: 260, overflowY: "auto" }}>
                  <BasicDetailsSidebar empId={empId} folder={folder} employeeRole={employeeRole} />
                </Box>
              )}
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                p: 2,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {isLoaded ? (
                <Loader />
              ) : unsupportedFormat ? (
                <Typography
                  sx={{ textAlign: "center", py: 4, color: "text.secondary" }}
                >
                  This file format is not supported for preview.
                </Typography>
              ) : (Array.isArray(file) ? file : [file]).filter(Boolean).length >
                0 ? (
                (Array.isArray(file) ? file : [file]).map((item, index) => {
                  const fileExtension = item.split(".").pop().toLowerCase();
                  return fileExtension === "pdf" ? (
                    <iframe
                      key={index}
                      src={blobToPdf}
                      width="100%"
                      height="100%"
                      title={`PDF Viewer ${index}`}
                      style={{ border: "none", flex: 1, minHeight: "480px" }}
                    />
                  ) : (
                    <Box
                      sx={{
                        pt: 2,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                        overflow: "hidden",
                      }}
                      key={index}
                    >
                      <img
                        src={imageUrls}
                        alt="Preview"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "480px",
                          objectFit: "contain",
                          borderRadius: "8px",
                        }}
                      />
                    </Box>
                  );
                })
              ) : (
                <Typography>No File Found</Typography>
              )}

              {moduleName === "Other Documents" &&
                employeeRole === "Quality Check" &&
                employeeRole !== "HRSS" &&
                empIdGenerateStatus &&
                (!qcStatus || qcStatus === "Pending") && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 2,
                      mt: 2,
                    }}
                  >
                    <Button
                      variant="contained"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onQcAction("Verified");
                      }}
                      sx={{
                        textTransform: "none",
                        backgroundColor: "#4caf50",
                        color: "#fff",
                        "&:hover": { backgroundColor: "#388e3c" },
                      }}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onQcAction("Rejected");
                      }}
                      sx={{
                        textTransform: "none",
                        backgroundColor: "#ef5350",
                        color: "#fff",
                        "&:hover": { backgroundColor: "#d32f2f" },
                      }}
                    >
                      Reject
                    </Button>
                  </Box>
                )}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
      {/* )} */}
    </>
  );
};

export default DocViewModal;
